import { useNavigate } from "react-router-dom";
import { Bot, ChevronLeft, Plus } from "lucide-react";
import TopHeader from "@/components/TopHeader";
import BottomNav from "@/components/BottomNav";
import { BotList } from "@/components/bots";
import { useBots } from "@/hooks/use-bots";
import { useSession } from "@/hooks/useSessionHook";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import supabase from "@/supabase";

export default function BotListPage() {
  const navigate = useNavigate();
  const { bots, isLoading, error } = useBots();
  const { profile } = useSession();
  const isAdmin = profile?.app_role === 'admin';
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const handleBotSelect = (botId: string) => {
    navigate(isAdmin ? `/bots/${botId}/manage` : `/bot/${botId}`);
  };

  const handleCreateBot = async () => {
    setIsCreating(true);
    setCreateError(null);

    try {
      const { data, error: createError } = await supabase.functions.invoke('admin-create-bot', {
        body: {},
      });

      if (createError) throw createError;
      if (!data?.bot?.id) {
        throw new Error('Bot creation failed.');
      }

      navigate(`/bots/${data.bot.id}/manage`);
    } catch (error) {
      console.error('Failed to create bot companion:', error);
      setCreateError(error instanceof Error ? error.message : 'Failed to create bot companion.');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="jr-app-shell">
      <TopHeader />

      <header className="jr-subheader">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/settingspage')}
          className="mr-2 rounded-full text-[var(--jr-text)] hover:bg-[color:rgba(255,255,255,0.06)]"
        >
          <ChevronLeft size={24} />
        </Button>
        <h2 className="jr-display text-2xl font-semibold">AI Companions</h2>
      </header>

      <main className="flex-1 overflow-y-auto pb-24">
        <div className="jr-screen-pad py-5">
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Bot className="h-6 w-6 text-[var(--jr-accent)]" />
              <h1 className="jr-section-title text-[2rem]">AI Companions</h1>
            </div>
            {isAdmin && (
              <Button onClick={() => void handleCreateBot()} disabled={isCreating} className="jr-action-primary h-auto min-h-11">
                {isCreating ? null : <Plus className="mr-2 h-4 w-4" />}
                {isCreating ? 'Creating...' : 'New Companion'}
              </Button>
            )}
          </div>

          <p className="mb-6 max-w-md text-sm leading-6 text-[var(--jr-text-soft)]">
            {isAdmin
              ? 'Manage companion personas, media assets, and companion activity.'
              : 'Choose an AI companion to chat with. Each has a unique personality and style.'}
          </p>

          {createError && (
            <div className="jr-status-error mb-4 text-center">{createError}</div>
          )}

          {error && (
            <div className="jr-status-error mb-4 text-center">{error}</div>
          )}

          <BotList
            bots={bots}
            isLoading={isLoading}
            onBotSelect={handleBotSelect}
          />
        </div>
      </main>

      <BottomNav />
    </div>
  );
}
